import { Typography } from '@material-ui/core';
import { Link } from 'gatsby';
import {
  ArrowRight,
  Box,
  Check,
  Sparkles,
  Download,
  DollarSign,
  File,
  GitMerge,
  Layers,
  LayoutDashboard,
  Link as LinkIcon,
  Lock,
  Minimize2,
  MousePointerClick,
  Newspaper,
  Percent,
  ChartPie as PieChart,
  Presentation,
  Rewind,
  Share2,
  Shuffle,
  Smartphone,
  SquarePen as Edit,
  Target,
  Terminal,
  Users,
  Zap
} from 'lucide-react';
import React from 'react';
import { CampaignManagementMockup } from '../components/AmplifyProductMockups/CampaignManagementMockup';
import { DemoButton } from '../components/DemoButton';
import { Headline } from '../components/Headline';
import { Kicker } from '../components/Kicker';
import { mobileHeroLeadText, mobileLeadText } from '../components/MarketingTypography';
import { GradientBackground } from '../components/HeroSection/GradientBackground';
import { Reveal } from '../components/motion';
import { ProductIcon } from '../components/ProductIcon';
import { EarlyAccessBadge, NewBadge, NoCodeBadge } from '../components/NewBadge';
import { SEO } from '../components/SEO';
import MainLayout from '../layouts/main';
import styled from '../styled';
import { COLORS } from '../themes/color';

const INK = COLORS.INK;

const HeroSection = styled('section')`
  position: relative;
  overflow: hidden;
  padding: 120px 0 88px;
  text-align: center;

  @media (max-width: 800px) {
    padding: 88px 0 64px;
  }
`;

const HeroCopy = styled('div')`
  position: relative;
  z-index: 1;
  max-width: 760px;
  margin: 0 auto;
  padding: 0 24px;
`;

const HeroDescription = styled('p')`
  font-size: 19px;
  line-height: 1.6;
  color: #4f4f4f;
  max-width: 620px;
  margin: 24px auto 0;
  ${mobileHeroLeadText}
`;

const SolutionsSection = styled('section')`
  max-width: 1100px;
  margin: 96px auto;
  padding: 0 24px;
`;

const SolutionsGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-top: 48px;
  align-items: stretch;

  > * {
    height: 100%;
  }

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const solutionCardStyles = `
  box-sizing: border-box;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 28px 24px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(14, 28, 43, 0.04);
  transition: box-shadow 0.25s ease, border-color 0.25s ease,
    transform 0.25s ease;
`;

const SolutionCardLink = styled(Link)`
  ${solutionCardStyles}
  text-decoration: none;
  color: inherit;

  &:hover {
    border-color: ${COLORS.PRIMARY};
    box-shadow: 0 8px 30px rgba(46, 92, 255, 0.12);
    transform: translateY(-2px);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover {
      transform: none;
    }
  }
`;

const SolutionTitleRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const SolutionTitle = styled('h3')`
  font-size: 19px;
  font-weight: 700;
  color: ${INK};
  margin: 0;
`;

const SolutionBody = styled('p')`
  font-size: 14px;
  line-height: 1.6;
  color: #4f4f4f;
  margin: 0;
  flex: 1;
`;

const SolutionLinkRow = styled('div')`
  font-size: 14px;
  font-weight: 600;
  color: ${COLORS.PRIMARY};
  display: flex;
  align-items: center;
  gap: 6px;
`;

const AiPlatformCardLink = styled(Link)`
  ${solutionCardStyles}
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 20px;
  margin-top: 20px;
  text-decoration: none;
  color: inherit;

  &:hover {
    border-color: ${COLORS.PRIMARY};
    box-shadow: 0 8px 30px rgba(46, 92, 255, 0.12);
    transform: translateY(-2px);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover {
      transform: none;
    }
  }

  @media (max-width: 700px) {
    flex-direction: column;
  }
`;

const AiPlatformContent = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
`;

const CampaignSection = styled('section')`
  max-width: 1100px;
  margin: 0 auto 96px;
  padding: 0 24px;
  display: grid;
  grid-template-columns: 1fr 1.15fr;
  gap: 64px;
  align-items: center;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

const CampaignContent = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: flex-start;
`;

const CampaignBadgeRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const CampaignBody = styled('p')`
  font-size: 17px;
  line-height: 1.65;
  color: #4f4f4f;
  margin: 0;
`;

const CheckList = styled('ul')`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CheckItem = styled('li')`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  font-size: 15px;
  line-height: 1.5;
  color: #4f4f4f;

  svg {
    flex-shrink: 0;
    margin-top: 3px;
    color: ${COLORS.PRIMARY};
  }
`;

const InlineLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${COLORS.PRIMARY};
  font-weight: 600;
  font-size: 16px;
  text-decoration: none;

  &:hover {
    opacity: 0.8;
  }
`;

const FeaturesSection = styled('section')`
  max-width: 1100px;
  margin: 0 auto 96px;
  padding: 0 24px;
`;

const FeaturesHeader = styled('div')`
  text-align: center;
  max-width: 640px;
  margin: 0 auto 40px;
`;

const BentoGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;

  > * {
    height: 100%;
    min-width: 0;
  }

  @media (max-width: 960px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const bentoCardStyles = `
  height: 100%;
  box-sizing: border-box;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 28px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  transition: box-shadow 0.2s, border-color 0.2s, transform 0.2s;
`;

const BentoCard = styled('div')`
  ${bentoCardStyles}
`;

const BentoTitle = styled('h3')`
  font-size: 17px;
  font-weight: 700;
  color: ${INK};
  margin: 0;
`;

const BentoBody = styled('p')`
  font-size: 14px;
  line-height: 1.6;
  color: #64748b;
  margin: 0;
  flex: 1;
`;

const FinalCta = styled('div')`
  text-align: center;
  max-width: 640px;
  margin: 0 auto 120px;
  padding: 0 24px;
`;

const FinalCtaBody = styled('p')`
  font-size: 17px;
  line-height: 1.65;
  color: #64748b;
  margin: 16px auto 28px;
  max-width: 560px;
  ${mobileLeadText}
`;

type FeatureItem = {
  icon: React.ComponentType<{ size?: string | number }>;
  title: string;
  body: string;
};

type FeatureCategory = {
  headline: string;
  intro: string;
  features: FeatureItem[];
};

const FEATURE_CATEGORIES: FeatureCategory[] = [
  {
    headline: 'Content insights and optimization',
    intro:
      'Editor-friendly tools that teams will actually adopt and use to improve content performance.',
    features: [
      {
        icon: File,
        title: 'In-Article Analytics',
        body:
          'Detailed, link-level in-article analytics presented in a user-friendly way.'
      },
      {
        icon: PieChart,
        title: 'Scalable Segmentation',
        body:
          'Segment your affiliate content by content format, product category, and more.'
      },
      {
        icon: DollarSign,
        title: 'Granular Attribution',
        body:
          'See affiliate performance by site, page, link, element, merchant and more.'
      },
      {
        icon: Target,
        title: 'Conversion-aware Heatmaps',
        body:
          'Heatmaps of affiliate conversions and engagement within your articles.'
      },
      {
        icon: GitMerge,
        title: 'Traffic Sources & UTMs',
        body:
          'Track affiliate earnings back to traffic source, UTM, and paid campaigns.'
      },
      {
        icon: Rewind,
        title: 'Revisions',
        body: 'Run experiments and monitor key metrics for each content edit.'
      },
      {
        icon: Share2,
        title: 'Social Channel Tracking',
        body:
          'See revenue from social channels like Instagram and YouTube next to your on-site performance.'
      },
      {
        icon: Newspaper,
        title: 'Syndication Tracking',
        body:
          'Follow performance of content syndicated to partners like Apple News and MSN.'
      },
      {
        icon: Smartphone,
        title: 'Mobile App Tracking',
        body:
          'Track affiliate clicks and revenue from your mobile apps alongside your website.'
      }
    ]
  },
  {
    headline: 'Workflow and collaboration',
    intro:
      'Improve efficiency and eliminate errors with team collaboration and link management tools.',
    features: [
      {
        icon: Edit,
        title: 'Author Insights',
        body:
          'Benchmark author performance for key affiliate metrics automatically.'
      },
      {
        icon: Zap,
        title: 'Affiliate Link Generator',
        body:
          'Scale your link creation with the universal affiliate link generator tool or API.'
      },
      {
        icon: Percent,
        title: 'Rate Tracking',
        body: 'Monitor your rates cross-network with daily updates.'
      },
      {
        icon: LinkIcon,
        title: 'Affiliate Link Management',
        body:
          'Automate your affiliate link management, including with custom redirects.'
      },
      {
        icon: Users,
        title: 'Teams & Workspaces',
        body:
          'Invite team members to your workspace and manage multiple workspaces.'
      },
      {
        icon: Lock,
        title: 'Custom Roles & Permissions',
        body:
          'Design custom user roles giving just the level of visibility needed.'
      }
    ]
  },
  {
    headline: 'Reporting and finance',
    intro: 'Track your revenue, commissions, and payouts with ease.',
    features: [
      {
        icon: LayoutDashboard,
        title: 'Unified Dashboard',
        body:
          'Aggregate all your affiliate commissions in one, unified dashboard.'
      },
      {
        icon: Terminal,
        title: 'Advanced APIs',
        body:
          'Pull your data into a custom warehouse or use it to promote trending content.'
      },
      {
        icon: Shuffle,
        title: 'Payouts & Reconciliation',
        body:
          'Streamline finance processes with payout reporting and reconciliation.'
      },
      {
        icon: Download,
        title: 'Comprehensive Export',
        body:
          'Export any report or dataset to CSV or Excel for further analysis.'
      },
      {
        icon: Minimize2,
        title: '100+ Integrations',
        body:
          'Consolidate earnings from 100+ affiliate networks, platforms, and data sources.'
      },
      {
        icon: Box,
        title: 'Custom Integrations',
        body:
          'Expertly maintained custom integrations for the full revenue picture.'
      }
    ]
  }
];

export default () => {
  return (
    <MainLayout>
      <div>
        <SEO
          pathname="/product/"
          title="Affiliate Tracking for Publishers · Affilimate Features"
          description="Unified affiliate analytics, content optimization, sponsorship campaign infrastructure, and an AI platform built on your commerce data."
        />

        <HeroSection>
          <GradientBackground />
          <HeroCopy>
            <Reveal>
              <Headline
                variant="h1"
                component="h1"
                style={{
                  maxWidth: '700px',
                  margin: '0 auto',
                  fontWeight: 900
                }}
              >
                Affiliate growth tools for leading publishers
              </Headline>
              <HeroDescription>
                Find revenue opportunities in your existing content, links, and
                partnerships. Start with one solution or combine them as you
                grow.
              </HeroDescription>
              <div style={{ textAlign: 'center', margin: '48px auto 0' }}>
                <DemoButton text="Get a demo" variant="contained" />
              </div>
            </Reveal>
          </HeroCopy>
        </HeroSection>

        <SolutionsSection>
          <Reveal>
            <div style={{ textAlign: 'center' }}>
              <Headline component="h2">Publisher solutions</Headline>
              <Typography
                variant="body1"
                color="textSecondary"
                style={{ maxWidth: '600px', margin: '16px auto 0' }}
              >
                Drive growth across monetization models and channels with a
                single platform. Unified data, editorial optimization, and
                campaigns on the same commerce data.
              </Typography>
            </div>
          </Reveal>

          <SolutionsGrid>
            <Reveal delay={0} style={{ height: '100%' }}>
              <SolutionCardLink to="/product/unify/">
                <ProductIcon $size="lg">
                  <Layers size={20} />
                </ProductIcon>
                <SolutionTitle>Unify</SolutionTitle>
                <SolutionBody>
                  All your affiliate transactions, rates, and payouts from 100+
                  networks in one place, available via API, MCP, and data
                  warehouse connectors.
                </SolutionBody>
                <SolutionLinkRow>
                  Explore Unify
                  <ArrowRight size={14} />
                </SolutionLinkRow>
              </SolutionCardLink>
            </Reveal>

            <Reveal delay={80} style={{ height: '100%' }}>
              <SolutionCardLink to="/product/optimize/">
                <ProductIcon $size="lg">
                  <MousePointerClick size={20} />
                </ProductIcon>
                <SolutionTitle>Optimize</SolutionTitle>
                <SolutionBody>
                  Content optimization for editorial teams: heatmaps, content
                  trends, segmentation, revision tracking, and traffic source
                  reporting.
                </SolutionBody>
                <SolutionLinkRow>
                  Explore Optimize
                  <ArrowRight size={14} />
                </SolutionLinkRow>
              </SolutionCardLink>
            </Reveal>

            <Reveal delay={160} style={{ height: '100%' }}>
              <SolutionCardLink to="/product/amplify/">
                <ProductIcon $size="lg">
                  <Presentation size={20} />
                </ProductIcon>
                <SolutionTitleRow>
                  <SolutionTitle>Amplify</SolutionTitle>
                  <NewBadge>New</NewBadge>
                </SolutionTitleRow>
                <SolutionBody>
                  Sponsorship campaigns and yield optimization under one roof,
                  from pitch to flight to renewal.
                </SolutionBody>
                <SolutionLinkRow>
                  Explore Amplify
                  <ArrowRight size={14} />
                </SolutionLinkRow>
              </SolutionCardLink>
            </Reveal>
          </SolutionsGrid>

          <Reveal delay={240}>
            <AiPlatformCardLink to="/ai-platform/">
              <ProductIcon $size="lg">
                <Sparkles size={20} />
              </ProductIcon>
              <AiPlatformContent>
                <SolutionTitleRow>
                  <SolutionTitle>AI platform</SolutionTitle>
                  <EarlyAccessBadge />
                </SolutionTitleRow>
                <SolutionBody>
                  Affilimate Agent, MCP, campaign execution, agent analytics,
                  and Yield optimization, built on the same commerce data.
                  Your team approves every change before it goes live.
                </SolutionBody>
                <SolutionLinkRow>
                  Explore the AI platform
                  <ArrowRight size={14} />
                </SolutionLinkRow>
              </AiPlatformContent>
            </AiPlatformCardLink>
          </Reveal>
        </SolutionsSection>

        <CampaignSection>
          <Reveal>
            <CampaignContent>
              <CampaignBadgeRow>
                <Kicker style={{ marginBottom: 0 }}>Sponsorship campaigns</Kicker>
                <NoCodeBadge />
              </CampaignBadgeRow>
              <Headline component="h2" variant="h3" style={{ margin: 0 }}>
                Sell and prove more sponsorship campaigns
              </Headline>
              <CampaignBody>
                Amplify puts campaign infrastructure on top of your own
                performance history, so your team can scope, flight, and prove
                every deal in the same platform that measures it.
              </CampaignBody>
              <CheckList>
                <CheckItem>
                  <Check size={16} />
                  Pitch with evidence from your own page and link history
                </CheckItem>
                <CheckItem>
                  <Check size={16} />
                  Offer campaigns on a CPA, CPC, or flat spend basis
                </CheckItem>
                <CheckItem>
                  <Check size={16} />
                  See in-flight delivery while campaigns are still running
                </CheckItem>
                <CheckItem>
                  <Check size={16} />
                  Walk into renewals with numbers your clients trust
                </CheckItem>
                <CheckItem>
                  <Check size={16} />
                  Enrich campaigns with product data from 70M+ tracked products
                  matched to your links
                </CheckItem>
              </CheckList>
              <InlineLink to="/product/amplify/">
                Explore Amplify <ArrowRight size={18} />
              </InlineLink>
            </CampaignContent>
          </Reveal>
          <Reveal delay={150}>
            <CampaignManagementMockup />
          </Reveal>
        </CampaignSection>

        {FEATURE_CATEGORIES.map((category) => (
          <FeaturesSection key={category.headline}>
            <Reveal>
              <FeaturesHeader>
                <Headline component="h2" style={{ margin: 0 }}>
                  {category.headline}
                </Headline>
                <Typography
                  variant="body1"
                  color="textSecondary"
                  style={{ maxWidth: '510px', margin: '16px auto 0' }}
                >
                  {category.intro}
                </Typography>
              </FeaturesHeader>
            </Reveal>
            <BentoGrid>
              {category.features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <Reveal
                    key={feature.title}
                    delay={index * 60}
                    style={{ height: '100%' }}
                  >
                    <BentoCard>
                      <ProductIcon>
                        <Icon size={20} />
                      </ProductIcon>
                      <BentoTitle>{feature.title}</BentoTitle>
                      <BentoBody>{feature.body}</BentoBody>
                    </BentoCard>
                  </Reveal>
                );
              })}
            </BentoGrid>
          </FeaturesSection>
        ))}

        <FinalCta>
          <Reveal>
            <Headline component="h2" style={{ margin: 0 }}>
              Discover even more
            </Headline>
            <FinalCtaBody>
              Discover more ways publishers are growing with Affilimate by
              booking a demo with our team today.
            </FinalCtaBody>
            <DemoButton text="Get a demo" variant="contained" />
          </Reveal>
        </FinalCta>
      </div>
    </MainLayout>
  );
};
